// src/components/pages/admin-dash/AdminTable.jsx
import React, { useMemo, useState } from "react";
import Icon from "../../ui/Icon";
import Spinner from "../../ui/Spinner";

/**
 * Shared admin table with:
 *   - Sortable columns (click header)
 *   - Client-side pagination (page-size selector)
 *   - Loading / empty / error states
 *   - Themed via CSS variables (dark mode "just works")
 *
 * Column shape:
 *   {
 *     key:      "id"                          // accessor key on row
 *     label:    "ID"                          // header label
 *     sort:     true                          // sortable?
 *     align:    "left" | "right" | "center"   // default "left"
 *     width:    "60px"                        // optional fixed width
 *     render:   (row) => ReactNode            // optional cell renderer
 *     value:    (row) => any                  // optional sort/value accessor
 *   }
 */
export default function AdminTable({
  columns,
  rows,
  loading = false,
  error = "",
  emptyMessage = "Nothing here yet.",
  emptyIcon = "database",
  rowKey = "id",
  onRowClick,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 25,
  defaultSort = null,           // { key, dir: "asc" | "desc" }
  rightHeaderSlot = null,       // e.g. an Export button
  title,                        // optional table title
  subtitle,                     // optional subtitle under title
}) {
  const [sort, setSort]       = useState(defaultSort);
  const [page, setPage]       = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const toggleSort = (col) => {
    if (!col.sort) return;
    setPage(0);
    setSort((s) => {
      if (!s || s.key !== col.key) return { key: col.key, dir: "asc" };
      if (s.dir === "asc")         return { key: col.key, dir: "desc" };
      return null; // 3rd click clears
    });
  };

  const valueOf = (col, row) => {
    if (typeof col.value === "function") return col.value(row);
    return row?.[col.key];
  };

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = valueOf(col, a);
      const bv = valueOf(col, b);
      // null/undefined sort to bottom regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [rows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages - 1);
  const slice      = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const showHeader = title || subtitle || rightHeaderSlot;

  return (
    <div>
      {showHeader && (
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            {title && (
              <h2
                className="font-semibold"
                style={{ fontSize: 18, color: "var(--txt)" }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
                {subtitle}
              </p>
            )}
          </div>
          {rightHeaderSlot}
        </div>
      )}

      <div
        className="overflow-hidden"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)",
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr>
                {columns.map((c) => {
                  const isSorted = sort?.key === c.key;
                  const align = c.align || "left";
                  return (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c)}
                      className={`px-4 py-3 font-medium select-none ${
                        c.sort ? "cursor-pointer hover:bg-[var(--surf-2)]" : ""
                      }`}
                      style={{
                        textAlign: align,
                        color: "var(--txt-2)",
                        borderBottom: "1px solid var(--border)",
                        background: "var(--surf-2)",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        width: c.width,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sort && (
                          <span
                            style={{
                              opacity: isSorted ? 1 : 0.3,
                              color: isSorted ? "var(--gold)" : "var(--txt-3)",
                              fontSize: 10,
                              lineHeight: 1,
                              display: "inline-block",
                              transform:
                                isSorted && sort.dir === "desc"
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                              transition: "transform 0.15s",
                            }}
                          >
                            ▲
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center">
                    <div
                      className="inline-flex items-center gap-2"
                      style={{ color: "var(--txt-2)" }}
                    >
                      <Spinner size={16} />
                      <span className="text-[13px]">Loading…</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center">
                    <Icon
                      name="alert-circle"
                      size={26}
                      style={{ color: "var(--red)" }}
                    />
                    <div
                      className="text-[13px] mt-2"
                      style={{ color: "var(--red)" }}
                    >
                      {error}
                    </div>
                  </td>
                </tr>
              ) : slice.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center">
                    <Icon
                      name={emptyIcon}
                      size={28}
                      style={{ color: "var(--txt-3)" }}
                    />
                    <div
                      className="text-[13px] mt-2"
                      style={{ color: "var(--txt-2)" }}
                    >
                      {emptyMessage}
                    </div>
                  </td>
                </tr>
              ) : (
                slice.map((row, idx) => (
                  <tr
                    key={row?.[rowKey] ?? idx}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={onRowClick ? "cursor-pointer" : ""}
                    style={{
                      borderBottom:
                        idx === slice.length - 1
                          ? "none"
                          : "1px solid var(--border)",
                      transition: "background-color 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      if (onRowClick)
                        e.currentTarget.style.backgroundColor = "var(--surf-2)";
                    }}
                    onMouseLeave={(e) => {
                      if (onRowClick) e.currentTarget.style.backgroundColor = "";
                    }}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className="px-4 py-3"
                        style={{
                          color: "var(--txt)",
                          textAlign: c.align || "left",
                          whiteSpace: c.wrap ? "normal" : "nowrap",
                        }}
                      >
                        {c.render ? c.render(row) : valueOf(c, row) ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        {!loading && !error && sorted.length > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            style={{
              borderTop: "1px solid var(--border)",
              background: "var(--surf-2)",
            }}
          >
            <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>
              Showing {safePage * pageSize + 1}–
              {Math.min((safePage + 1) * pageSize, sorted.length)} of{" "}
              {sorted.length}
            </div>

            <div className="flex items-center gap-3">
              <label
                className="inline-flex items-center gap-2 text-[11px]"
                style={{ color: "var(--txt-3)" }}
              >
                Rows
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(0);
                    setPageSize(Number(e.target.value));
                  }}
                  className="px-2 py-1 rounded-md text-[12px] outline-none"
                  style={{
                    background: "var(--surf)",
                    color: "var(--txt)",
                    border: "1px solid var(--border-2)",
                  }}
                >
                  {pageSizeOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex items-center gap-1">
                <PagerButton
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  icon="arrow-left"
                />
                <span
                  className="text-[12px] tabular-nums px-2"
                  style={{ color: "var(--txt-2)" }}
                >
                  {safePage + 1} / {totalPages}
                </span>
                <PagerButton
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setPage(safePage + 1)}
                  icon="arrow-right"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PagerButton({ disabled, onClick, icon }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md transition-colors"
      style={{
        width: 26,
        height: 26,
        border: "1px solid var(--border-2)",
        background: "var(--surf)",
        color: disabled ? "var(--txt-3)" : "var(--txt-2)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Icon name={icon} size={12} />
    </button>
  );
}
